import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Delete, Edit, Print } from '@mui/icons-material';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    ThemeProvider,
    Tooltip,
    MenuItem,
    ListItemIcon,
    ListItemText,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';

import * as UnidadeCondominialProdutoAPI from '../../../api/unidadeCondominialProduto';
import { Context } from '../../../context/UserContext';
import { getEmpresaScope } from '../../../helpers/sessionEmpresa';
import { getMRTDefaultProps } from '../../../helpers/mrt/getMRTDefaultProps';
import { useMRTState } from '../../../hooks/useMRTState';
import { podeCancelar, podeEditar, podeImprimir, podeIncluir } from '../../../hooks/usePermisionMRT';
import { useNavigateParams } from '../../../hooks/useNavigateParams';
import useFlashMessage from '../../../hooks/useFlashMessage';
import { obterPermissoesUsuario } from '../../../utils/funcoes';
import tableThemeCustom from '../../form/tableThemeCustom';

function formatarData(value) {
    if (!value) return '';

    const [ano, mes, dia] = String(value).slice(0, 10).split('-');

    return ano && mes && dia ? `${dia}/${mes}/${ano}` : '';
}

function formatarDataHora(value) {
    if (!value) return '';

    const data = new Date(value);

    return Number.isNaN(data.getTime()) ? '' : data.toLocaleString('pt-BR');
}

function formatarQuantidade(value) {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    const quantidade = Number(value);

    if (!Number.isFinite(quantidade)) {
        return '';
    }

    return quantidade.toLocaleString('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 3,
    });
}

function Index() {
    const { parametro } = useContext(Context);
    const { pessoaId, empresaId } = getEmpresaScope();
    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams();

    const urlParams = new URLSearchParams(window.location.search);
    const ul = urlParams.get('ul');
    const li = urlParams.get('li');

    const [token] = useState(localStorage.getItem('token') || '');
    const [dados, setDados] = useState([]);
    const [vinculoExcluir, setVinculoExcluir] = useState(null);
    const [excluindo, setExcluindo] = useState(false);
    const [dialogRelatorio, setDialogRelatorio] = useState(false);
    const [gerandoRelatorio, setGerandoRelatorio] = useState(false);
    const [somenteDuplicidades, setSomenteDuplicidades] = useState(false);
    const relatorioEmAndamento = useRef(false);

    const mrt = useMRTState({
        storageKey: 'unidade-condominial-produto.index',
        initialState: {
            columnFilterFns: {},
            columnFilters: [],
            globalFilter: '',
            sorting: [],
            pagination: {
                pageIndex: 0,
                pageSize: parametro?.paginacao || 10,
            },
        },
    });

    const {
        columnFilterFns,
        columnFilters,
        globalFilter,
        sorting,
        pagination,
        setIsLoading,
        setIsError,
        setIsRefetching,
        setRowCount,
    } = mrt;

    const themeMRT = useTheme();
    const isMobile = useMediaQuery(themeMRT.breakpoints.down('sm'));

    const mrtDefault = getMRTDefaultProps({
        mrt,
        parametro,
        ...(isMobile ? { isMobile: true } : {}),
    });

    async function fetchData() {
        dados.length ? setIsRefetching(true) : setIsLoading(true);

        try {
            const response = await UnidadeCondominialProdutoAPI.listar({
                start: pagination.pageIndex * pagination.pageSize,
                size: pagination.pageSize,
                filters: JSON.stringify(columnFilters),
                filtersMode: JSON.stringify(columnFilterFns),
                globalFilter: globalFilter || '',
                sorting: JSON.stringify(sorting),
            });

            setDados(response.data.data || []);
            setRowCount(Number(response.data.meta?.totalRowCount) || 0);
            setIsError(false);
        } catch (error) {
            setIsError(true);

            setFlashMessage(
                error.response?.data?.message || 'Não foi possível consultar os serviços das unidades condominiais.',
                error.response?.data?.type || 'error'
            );
        } finally {
            setIsLoading(false);
            setIsRefetching(false);
        }
    }

    useEffect(() => {
        if (!token) {
            navigate('/Auth', {
                replace: true,
            });

            return;
        }

        fetchData();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, columnFilterFns, columnFilters, globalFilter, pagination.pageIndex, pagination.pageSize, sorting]);

    useEffect(() => {
        if (!pessoaId || !empresaId || !ul || !li) {
            return;
        }

        obterPermissoesUsuario(pessoaId, empresaId, ul, li);
    }, [pessoaId, empresaId, ul, li]);

    function novoVinculo() {
        const params = new URLSearchParams(urlParams);

        params.delete('uuid');
        params.set('btnText', 'Cadastrar');

        navigate('/UnidadeCondominialProdutos/CreateOrEdit', params);
    }

    function editarVinculo(vinculo) {
        const params = new URLSearchParams(urlParams);

        params.set('uuid', vinculo.uuid);
        params.set('btnText', 'Atualizar');

        navigate('/UnidadeCondominialProdutos/CreateOrEdit', params);
    }

    async function confirmarExclusao() {
        if (!vinculoExcluir?.uuid || excluindo) {
            return;
        }

        setExcluindo(true);

        try {
            const response = await UnidadeCondominialProdutoAPI.excluir(vinculoExcluir.uuid);

            setVinculoExcluir(null);
            setFlashMessage(response.data.message, response.data.type);

            await fetchData();
        } catch (error) {
            setFlashMessage(
                error.response?.data?.message || 'Não foi possível excluir o serviço da unidade condominial.',
                error.response?.data?.type || 'error'
            );
        } finally {
            setExcluindo(false);
        }
    }

    async function gerarRelatorio() {
        if (relatorioEmAndamento.current || !podeImprimir()) return;
        relatorioEmAndamento.current = true;
        setGerandoRelatorio(true);

        // A janela nasce no clique para evitar bloqueio após a consulta assíncrona.
        // Se o navegador bloquear, o mesmo PDF será disponibilizado por download.
        let janela = null;
        try {
            janela = window.open('', '_blank');
            if (janela) {
                janela.opener = null;
                janela.document.title = 'Gerando relatório...';
                janela.document.body.textContent = 'Gerando relatório de serviços por unidade condominial...';
            }
            const response = await UnidadeCondominialProdutoAPI.relatorio({
                filters: JSON.stringify(columnFilters),
                filtersMode: JSON.stringify(columnFilterFns),
                globalFilter: globalFilter || '',
                sorting: JSON.stringify(sorting),
                somenteDuplicidades,
            });
            const arquivo = new Blob([response.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(arquivo);
            try {
                if (janela && !janela.closed) {
                    janela.location.href = url;
                } else {
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = 'servicos-por-unidade-condominial.pdf';
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    setFlashMessage('Relatório gerado. O PDF foi disponibilizado para download.', 'success');
                }
            } finally {
                window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
            }
            setDialogRelatorio(false);
        } catch (error) {
            if (janela && !janela.closed) janela.close();
            let resposta = error.response?.data;
            if (resposta instanceof Blob) {
                try {
                    resposta = JSON.parse(await resposta.text());
                } catch {
                    resposta = null;
                }
            }
            setFlashMessage(
                resposta?.message || 'Não foi possível gerar o relatório de serviços por unidade condominial.',
                resposta?.type || 'error'
            );
        } finally {
            relatorioEmAndamento.current = false;
            setGerandoRelatorio(false);
        }
    }

    const columns = useMemo(
        () => [
            {
                id: 'unidadeCondominialProdutos',
                header: 'SERVIÇOS POR UNIDADE CONDOMINIAL',
                columns: [
                    { accessorKey: 'id', header: 'Vínculo', size: 90 },
                    {
                        accessorKey: 'Empresa.nomeFantasia',
                        header: 'Empresa',
                    },
                    {
                        accessorKey: 'UnidadeCondominial.codigo',
                        header: 'Unidade',
                    },
                    {
                        accessorKey: 'UnidadeCondominial.descricao',
                        header: 'Descrição da unidade',
                    },
                    {
                        accessorKey: 'UnidadeCondominial.quadra',
                        header: 'Quadra',
                        size: 90,
                    },
                    {
                        accessorKey: 'UnidadeCondominial.lote',
                        header: 'Lote',
                        size: 90,
                    },
                    {
                        accessorKey: 'Produto.codigo',
                        header: 'Código',
                        size: 100,
                    },
                    {
                        accessorKey: 'Produto.descricao',
                        header: 'Serviço',
                    },
                    {
                        accessorKey: 'tabelaPreco.descricao',
                        header: 'Tabela de preços',
                    },
                    {
                        accessorKey: 'Embalagem.descricao',
                        header: 'Embalagem',
                    },
                    {
                        accessorKey: 'quantidade',
                        header: 'Quantidade',
                        Cell: ({ cell }) => formatarQuantidade(cell.getValue()),
                        muiTableBodyCellProps: {
                            align: 'right',
                        },
                        muiTableHeadCellProps: {
                            align: 'right',
                        },
                    },
                    {
                        accessorKey: 'dataInicio',
                        header: 'Início',
                        Cell: ({ cell }) => formatarData(cell.getValue()),
                    },
                    {
                        accessorKey: 'dataFim',
                        header: 'Término',
                        Cell: ({ cell }) => formatarData(cell.getValue()),
                    },
                    {
                        accessorKey: 'ativo',
                        header: 'Ativo',
                        type: 'boolean',
                        columnFilterModeOptions: ['equals'],
                        filterSelectOptions: [
                            { label: 'Todos', value: '' },
                            { label: 'Sim', value: '1' },
                            { label: 'Não', value: '0' },
                        ],
                        filterVariant: 'select',
                        Cell: ({ row }) => {
                            const value = row.original.ativo;
                            return <div style={{ textAlign: 'left' }}>{value ? 'Sim' : 'Não'}</div>;
                        },
                    },
                    {
                        accessorKey: 'updatedAt',
                        header: 'Atualizado em',
                        Cell: ({ cell }) => formatarDataHora(cell.getValue()),
                    },
                ],
            },
        ],
        []
    );

    const theme = tableThemeCustom();

    return (
        <div>
            <ThemeProvider theme={theme}>
                <MaterialReactTable
                    {...mrtDefault}
                    columns={columns}
                    data={dados}
                    getRowId={(row) => row.uuid}
                    renderTopToolbarCustomActions={() => (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button color="secondary" variant="contained" disabled={!podeIncluir()} onClick={novoVinculo}>
                                Novo
                            </Button>
                            <Button
                                color="info"
                                variant="contained"
                                startIcon={<Print />}
                                disabled={!podeImprimir() || gerandoRelatorio}
                                onClick={() => setDialogRelatorio(true)}
                                title="Gerar PDF de todos os vínculos dos filtros atuais"
                            >
                                {gerandoRelatorio ? 'Gerando...' : 'Relatório'}
                            </Button>
                        </Box>
                    )}
                    renderRowActions={
                        !isMobile
                            ? ({ row }) => (
                                  <Box
                                      sx={{
                                          display: 'flex',
                                          gap: 1,
                                      }}
                                  >
                                      <Tooltip title="Editar serviço da unidade">
                                          <span>
                                              <IconButton
                                                  color="primary"
                                                  disabled={!podeEditar()}
                                                  onClick={() => editarVinculo(row.original)}
                                              >
                                                  <Edit />
                                              </IconButton>
                                          </span>
                                      </Tooltip>

                                      <Tooltip title="Excluir serviço da unidade">
                                          <span>
                                              <IconButton
                                                  color="error"
                                                  disabled={!podeCancelar()}
                                                  onClick={() => setVinculoExcluir(row.original)}
                                              >
                                                  <Delete />
                                              </IconButton>
                                          </span>
                                      </Tooltip>
                                  </Box>
                              )
                            : undefined
                    }
                    renderRowActionMenuItems={
                        isMobile
                            ? ({ row, closeMenu }) => [
                                  <MenuItem
                                      key="editar"
                                      disabled={!podeEditar()}
                                      onClick={(event) => {
                                          event.preventDefault();
                                          event.stopPropagation();
                                          editarVinculo(row.original);
                                          closeMenu();
                                      }}
                                  >
                                      <ListItemIcon>
                                          <Edit color="primary" fontSize="small" />
                                      </ListItemIcon>
                                      <ListItemText>Editar</ListItemText>
                                  </MenuItem>,
                                  <MenuItem
                                      key="excluir"
                                      disabled={!podeCancelar()}
                                      onClick={(event) => {
                                          event.preventDefault();
                                          event.stopPropagation();
                                          setVinculoExcluir(row.original);
                                          closeMenu();
                                      }}
                                  >
                                      <ListItemIcon>
                                          <Delete color="error" fontSize="small" />
                                      </ListItemIcon>
                                      <ListItemText>Excluir</ListItemText>
                                  </MenuItem>,
                              ]
                            : undefined
                    }
                />
            </ThemeProvider>

            <Dialog open={dialogRelatorio} onClose={() => !gerandoRelatorio && setDialogRelatorio(false)} fullWidth maxWidth="sm">
                <DialogTitle>Serviços por unidade condominial</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        O PDF inclui todas as páginas dos filtros atuais, na ordenação da grade, com unidade, serviço, tabela de
                        preços, quantidade e vigência.
                    </DialogContentText>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={somenteDuplicidades}
                                disabled={gerandoRelatorio}
                                onChange={(event) => setSomenteDuplicidades(event.target.checked)}
                            />
                        }
                        label="Somente possíveis duplicidades"
                    />
                    <DialogContentText sx={{ mt: 1 }}>
                        A conferência considera vínculos ativos do mesmo serviço na mesma unidade com períodos sobrepostos,
                        inclusive em outras tabelas de preços. Vínculos conflitantes fora dos filtros serão indicados pelo número.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button disabled={gerandoRelatorio} onClick={() => setDialogRelatorio(false)}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<Print />}
                        disabled={gerandoRelatorio || !podeImprimir()}
                        onClick={gerarRelatorio}
                    >
                        {gerandoRelatorio ? 'Gerando...' : 'Gerar PDF'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(vinculoExcluir)}
                onClose={() => {
                    if (!excluindo) {
                        setVinculoExcluir(null);
                    }
                }}
            >
                <DialogTitle>Excluir serviço da unidade?</DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        O serviço <strong>{vinculoExcluir?.Produto?.descricao}</strong> será removido da unidade{' '}
                        <strong>
                            {vinculoExcluir?.UnidadeCondominial?.codigo} — {vinculoExcluir?.UnidadeCondominial?.descricao}
                        </strong>
                        .
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <Button disabled={excluindo} onClick={() => setVinculoExcluir(null)}>
                        Cancelar
                    </Button>

                    <Button variant="contained" color="error" disabled={excluindo} onClick={confirmarExclusao}>
                        {excluindo ? 'Excluindo...' : 'Excluir'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default Index;
